export const isConnectionError = error => error?.type === "api_connection_error"
    || error?.type === "network_error"
    || /issues connecting to our payments provider|failed to fetch|network request failed|networkerror/i.test(error?.message || "");

const wait = () => new Promise(resolve => setTimeout(resolve, 600));
const invoke = async operation => {
    try { return await operation(); }
    catch (error) { return { error }; }
};

export async function authorizeCard({ stripe, card, billingDetails, secret, delay = wait }) {
    let method = await invoke(() => stripe.createPaymentMethod({ type: "card", card, billing_details: billingDetails }));
    if (isConnectionError(method.error)) {
        await delay();
        method = await invoke(() => stripe.createPaymentMethod({ type: "card", card, billing_details: billingDetails }));
    }
    if (method.error) return method;
    if (!method.paymentMethod?.id) return { error: { message: "Card information could not be prepared. Please try again." } };

    for (let attempt = 0; attempt < 2; attempt++) {
        const result = await invoke(() => stripe.confirmCardSetup(secret, { payment_method: method.paymentMethod.id }));
        if (!result.error) return { ...result, paymentMethod: method.paymentMethod };
        if (!isConnectionError(result.error)) return result;
        // A confirmation can succeed even if its response is lost. Retrieve it
        // before retrying the same authorization and the same payment method.
        await delay();
        const recovered = await invoke(() => stripe.retrieveSetupIntent(secret));
        if (recovered.error) return recovered;
        const intent = recovered.setupIntent;
        const paymentId = typeof intent?.payment_method === "string" ? intent.payment_method : intent?.payment_method?.id;
        if (intent?.status === "succeeded" && paymentId === method.paymentMethod.id) {
            return { setupIntent: intent, paymentMethod: method.paymentMethod };
        }
        if (intent?.status === "processing") return { error: { message: "Card authorization is still processing. Please try again shortly." } };
        if (!['requires_payment_method', 'requires_confirmation', 'requires_action'].includes(intent?.status)) return result;
        if (intent.last_setup_error && !isConnectionError(intent.last_setup_error)) return { error: intent.last_setup_error };
        if (attempt === 1) return result;
    }
}

export function cardErrorMessage(error) {
    return isConnectionError(error)
        ? "The card processor could not be reached after retrying. Please try again. If this continues, check whether your browser or network is blocking Stripe."
        : error?.message || "Unable to save this card. Please try again.";
}
